import React, { useEffect, useState, memo } from 'react'
import {
    getServiceImageDetails,
    ListVendor,
    TemplateService,
} from '../../../../../models'
import {
    Button,
    Chip,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    TextField,
    Theme,
    Tooltip,
    MenuItem,
    Switch,
    FormControlLabel,
    CircularProgress,
    Typography,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { DateTime } from 'luxon'

import DeleteForeverIcon from '@material-ui/icons/DeleteForever'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import LinkIcon from '@material-ui/icons/Link'
import LinkOffIcon from '@material-ui/icons/LinkOff'
import InfoIcon from '@material-ui/icons/Info'
import { SelectorPopover } from '../../../../../components/TemplateScheduleGantt/Components/SelectorPopover'
import PersonAddIcon from '@material-ui/icons/PersonAdd'

import { Container } from '../../../../../components/Container'
import { usePopover } from '../../../../../hooks/usePopover'

import {
    DeleteTemplateServiceParams,
    AddTemplateServiceVendorParams,
    DeleteTemplateServiceVendorParams,
    UpdateTemplateServiceParams,
    UpdateTemplateServiceVendorParams,
} from '../../../../../hooks/useEzSchedule/types'
import { useAutoScheduleStyles } from '../styles'
import NumberFormat from 'react-number-format'
import { typographyStyle } from '../../../../../theme/typographyStyle'
import { useTimezone } from '../../../../../contexts/timezone'

const formatMoney = (amount: number) =>
    amount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    })

const formatBudgetInput = (budget: number | string | null | undefined) => {
    if (budget == null || budget === '') return ''
    const amount = Number(budget)
    if (!Number.isFinite(amount) || amount === 0) return ''
    return String(amount)
}

interface Props {
    theme: Theme
    templateService: TemplateService
    vendorList: ListVendor[]
    index: number
    serviceCount: number
    deleteTemplateService: (params: DeleteTemplateServiceParams) => void
    updateTemplateService: (params: UpdateTemplateServiceParams) => void
    addTemplateServiceVendor: (params: AddTemplateServiceVendorParams) => void
    deleteTemplateServiceVendor: (
        params: DeleteTemplateServiceVendorParams,
    ) => void
    updateTemplateServiceVendor: (
        params: UpdateTemplateServiceVendorParams,
    ) => void
    readOnly?: boolean
    showBudget?: boolean
    showAutoSelect?: boolean
    showVendorDates?: boolean
    areaCount?: number
    unitCount?: number
    estimatedCost?: number | null
    onAssignUnits?: (templateService: TemplateService) => void
}

export const ServiceRow = memo((props: Props) => {
    const {
        theme,
        templateService,
        vendorList,
        index,
        serviceCount,
        deleteTemplateService,
        updateTemplateService,
        addTemplateServiceVendor,
        deleteTemplateServiceVendor,
        updateTemplateServiceVendor,
        readOnly = false,
        showBudget = false,
        showAutoSelect = false,
        showVendorDates = false,
        areaCount,
        unitCount,
        estimatedCost,
        onAssignUnits,
    } = props

    const vendorPopoverState = usePopover()
    const classes = useAutoScheduleStyles()
    const { currentTimezone } = useTimezone()
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const [budgetText, setBudgetText] = useState(() =>
        formatBudgetInput(templateService.budget),
    )
    const open = Boolean(anchorEl)
    const isServicePending = templateService.id < 0

    useEffect(() => {
        setBudgetText(formatBudgetInput(templateService.budget))
    }, [templateService.id, templateService.budget])

    const commitBudget = () => {
        if (readOnly || isServicePending) return
        const trimmed = budgetText.trim()
        const next = trimmed === '' ? 0 : Number(trimmed)
        if (!Number.isFinite(next) || next < 0) {
            setBudgetText(formatBudgetInput(templateService.budget))
            return
        }
        const rounded = Math.round(next * 100) / 100
        if (rounded === Number(templateService.budget)) {
            setBudgetText(formatBudgetInput(rounded))
            return
        }
        setBudgetText(formatBudgetInput(rounded))
        updateTemplateService({
            templateServiceId: templateService.id,
            budget: rounded,
            stand_alone: templateService.stand_alone,
        })
    }

    const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    const countLabel = (count: number, singular: string, plural: string) =>
        `${count} ${count === 1 ? singular : plural}`

    const commitVendorDate = (
        templateServiceVendorId: number,
        field: 'start_date' | 'end_date',
        date: Date | null,
        boundary: 'start' | 'end',
    ) => {
        if (readOnly || date === null) return
        const zoned = DateTime.fromJSDate(date).setZone(currentTimezone, {
            keepLocalTime: true,
        })
        const next =
            boundary === 'start' ? zoned.startOf('day') : zoned.endOf('day')
        const iso = next.toUTC().toISO()
        if (!iso) return
        updateTemplateServiceVendor(
            field === 'start_date'
                ? {
                      templateServiceVendorId,
                      start_date: iso,
                  }
                : {
                      templateServiceVendorId,
                      end_date: iso,
                  },
        )
    }

    const pickerValue = (value: string) => {
        if (!value) return null
        const parsed = DateTime.fromISO(value).setZone(currentTimezone)
        if (!parsed.isValid) return null
        return parsed.setZone('local', { keepLocalTime: true })
    }

    const vendorOptions = vendorList.filter((vnd) => {
        const serviceOffered =
            vnd.services.find(
                (srv) => templateService.service.id === srv.id,
            ) !== undefined

        const alreadyIncluded =
            templateService.template_service_vendors.find(
                (tsv) => tsv.vendor.id === vnd.id,
            ) !== undefined

        return serviceOffered && !alreadyIncluded
    })

    const vendorRows = templateService.template_service_vendors.map((tsv) => {
        return (
            <Container
                key={`TEMPLATE-VENDOR-${tsv.id}`}
                className={`${classes.templateVendorBox} ${
                    showVendorDates ? classes.walkVendorBox : ''
                }`}
            >
                <span className={classes.templateVendorName}>
                    {tsv.vendor.name}
                </span>
                {showVendorDates && (
                    <>
                        <KeyboardDatePicker
                            className={classes.walkVendorDate}
                            label="Start"
                            format="MM/dd/yyyy"
                            value={pickerValue(tsv.start_date)}
                            disabled={readOnly || tsv.id < 0}
                            onChange={(date) =>
                                commitVendorDate(
                                    tsv.id,
                                    'start_date',
                                    date,
                                    'start',
                                )
                            }
                        />
                        <KeyboardDatePicker
                            className={classes.walkVendorDate}
                            label="End"
                            format="MM/dd/yyyy"
                            value={pickerValue(tsv.end_date)}
                            disabled={readOnly || tsv.id < 0}
                            onChange={(date) =>
                                commitVendorDate(
                                    tsv.id,
                                    'end_date',
                                    date,
                                    'end',
                                )
                            }
                        />
                    </>
                )}
                <div style={{ flex: 1 }} />
                {/* Auto Invoice Switch  */}
                <FormControlLabel
                    control={
                        <Switch
                            checked={tsv.auto_invoice}
                            disabled={readOnly}
                            onChange={(_e) => {
                                if (readOnly) return
                                updateTemplateServiceVendor({
                                    templateServiceVendorId: tsv.id,
                                    auto_invoice: _e.target.checked,
                                })
                            }}
                            color="primary"
                            size="small"
                        />
                    }
                    label={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span>Auto Invoice</span>
                            <Tooltip title="If turned on, all approved workorders by this vendor-service combination will be automatically invoiced at midnight UTC nightly">
                                <InfoIcon
                                    fontSize="small"
                                    style={{
                                        marginLeft: '4px',
                                        color: theme.palette.text.secondary,
                                        fontSize: '16px',
                                    }}
                                />
                            </Tooltip>
                        </div>
                    }
                />

                {readOnly ? null : (
                    <Tooltip title="Remove Vendor">
                        <IconButton
                            size="small"
                            onClick={() => {
                                deleteTemplateServiceVendor({
                                    templateServiceVendorId: tsv.id,
                                })
                            }}
                        >
                            <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Container>
        )
    })

    return (
        <Container className={classes.templateServiceContainer}>
            <div
                className={classes.templateServiceDraggable}
                style={{
                    background: templateService.stand_alone
                        ? theme.palette.action.hover
                        : theme.palette.background.paper,
                }}
            >
                <Container>
                    <div
                        style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <img
                            src={
                                getServiceImageDetails(
                                    templateService.service.id,
                                    templateService.service.service_category,
                                ).icon
                            }
                            style={{
                                maxHeight: 25,
                                maxWidth: 27,
                            }}
                        />
                        <div
                            style={{
                                flex: 1,
                                minWidth: 160,
                                maxWidth: 160,
                            }}
                        >
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.h5),
                                    marginLeft: theme.spacing(1),
                                }}
                            >
                                {templateService.service.name}
                            </span>
                        </div>

                        {showBudget && (
                            <NumberFormat
                                className={classes.serviceBudgetInput}
                                customInput={TextField}
                                variant="outlined"
                                label="Budget"
                                thousandSeparator
                                prefix="$"
                                decimalScale={2}
                                allowNegative={false}
                                disabled={readOnly || isServicePending}
                                value={budgetText}
                                onValueChange={(values) =>
                                    setBudgetText(values.value)
                                }
                                onBlur={commitBudget}
                            />
                        )}

                        {showAutoSelect && (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginLeft: theme.spacing(1),
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Switch
                                            color="primary"
                                            checked={Boolean(
                                                templateService.auto_select_areas,
                                            )}
                                            disabled={isServicePending}
                                            onChange={(_, checked) => {
                                                updateTemplateService({
                                                    templateServiceId:
                                                        templateService.id,
                                                    auto_select_areas: checked,
                                                })
                                            }}
                                        />
                                    }
                                    label="Auto select all valid areas"
                                />
                                <Tooltip title="On the phone, opening a unit selects every valid area for this service. Areas set to do not service are skipped. A paint service leaves an area that another paint service already has. The walker can still turn areas off.">
                                    <InfoIcon
                                        style={{
                                            fontSize: 18,
                                            color: theme.palette.grey[600],
                                            cursor: 'help',
                                        }}
                                    />
                                </Tooltip>
                            </div>
                        )}

                        {estimatedCost !== undefined && (
                            <>
                                <div
                                    style={{
                                        marginLeft: theme.spacing(2),
                                        minWidth: 120,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        display="block"
                                    >
                                        Estimated cost
                                    </Typography>
                                    <Typography style={{ fontWeight: 700 }}>
                                        {estimatedCost == null
                                            ? '—'
                                            : formatMoney(estimatedCost)}
                                    </Typography>
                                </div>
                                <div style={{ minWidth: 110 }}>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        display="block"
                                    >
                                        Variance
                                    </Typography>
                                    <Typography
                                        style={{
                                            fontWeight: 700,
                                            color:
                                                estimatedCost == null
                                                    ? undefined
                                                    : Number(
                                                          templateService.budget,
                                                      ) -
                                                          estimatedCost <
                                                      0
                                                    ? theme.palette.error.main
                                                    : theme.palette.success
                                                          .dark,
                                        }}
                                    >
                                        {estimatedCost == null
                                            ? '—'
                                            : formatMoney(
                                                  Number(
                                                      templateService.budget,
                                                  ) - estimatedCost,
                                              )}
                                    </Typography>
                                </div>
                            </>
                        )}

                        {templateService.stand_alone && (
                            <Chip
                                label="Stand Alone"
                                size="small"
                                variant="outlined"
                                icon={<LinkOffIcon style={{ fontSize: 14 }} />}
                                style={{ marginLeft: theme.spacing(1) }}
                            />
                        )}

                        <div style={{ flex: 1 }} />

                        {onAssignUnits &&
                            templateService.template_service_vendors.length >
                                1 && (
                                <Button
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                        onAssignUnits(templateService)
                                    }
                                    style={{
                                        textTransform: 'none',
                                        marginRight: theme.spacing(1),
                                    }}
                                >
                                    Assign Units
                                </Button>
                            )}

                        {isServicePending ? (
                            <CircularProgress size={20} />
                        ) : readOnly ? null : (
                            <>
                                {showBudget ? (
                                    <Button
                                        size="small"
                                        color="primary"
                                        startIcon={<PersonAddIcon />}
                                        onClick={vendorPopoverState.handleOpen}
                                        style={{
                                            textTransform: 'none',
                                            marginLeft: theme.spacing(1),
                                        }}
                                    >
                                        Add Vendor
                                    </Button>
                                ) : (
                                    <Tooltip title="Add Vendor">
                                        <IconButton
                                            size="small"
                                            onClick={
                                                vendorPopoverState.handleOpen
                                            }
                                        >
                                            <PersonAddIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <IconButton
                                    size="small"
                                    style={{ marginLeft: theme.spacing(1) }}
                                    onClick={openMenu}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            </>
                        )}
                    </div>
                </Container>
                {areaCount != null && unitCount != null && (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        style={{ marginLeft: theme.spacing(1) }}
                    >
                        {countLabel(areaCount, 'area', 'areas')} ·{' '}
                        {countLabel(unitCount, 'unit', 'units')}
                    </Typography>
                )}
                {vendorRows}
                <SelectorPopover
                    theme={theme}
                    popoverState={vendorPopoverState}
                    label="Select Vendor"
                    data={vendorOptions}
                    onSubmit={(vndId) => {
                        if (templateService.id < 0) {
                            return
                        }
                        addTemplateServiceVendor({
                            templateServiceId: templateService.id,
                            vendorId: vndId,
                            vendorName:
                                vendorOptions.find((vnd) => vnd.id === vndId)
                                    ?.name ?? '',
                            auto_invoice: false,
                        })
                    }}
                />
                {/* Service Row Menu, Move, Remove or make Stand Alone */}
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                    <MenuItem
                        disabled={index === 0}
                        onClick={() => {
                            updateTemplateService({
                                templateServiceId: templateService.id,
                                position: index - 1,
                            })
                            handleClose()
                        }}
                    >
                        <ListItemIcon>
                            <ArrowUpwardIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Move Up</ListItemText>
                    </MenuItem>
                    <MenuItem
                        disabled={index === serviceCount - 1}
                        onClick={() => {
                            updateTemplateService({
                                templateServiceId: templateService.id,
                                position: index + 1,
                            })
                            handleClose()
                        }}
                    >
                        <ListItemIcon>
                            <ArrowDownwardIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Move Down</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            updateTemplateService({
                                templateServiceId: templateService.id,
                                stand_alone: !templateService.stand_alone,
                            })
                            handleClose()
                        }}
                    >
                        <ListItemIcon>
                            {templateService.stand_alone ? (
                                <LinkIcon fontSize="small" />
                            ) : (
                                <LinkOffIcon fontSize="small" />
                            )}
                        </ListItemIcon>
                        <ListItemText>
                            {templateService.stand_alone
                                ? 'Set Linked'
                                : 'Set Stand Alone'}
                        </ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            deleteTemplateService({
                                templateServiceId: templateService.id,
                            })
                            handleClose()
                        }}
                    >
                        <ListItemIcon>
                            <DeleteForeverIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            Remove {templateService.service.name}
                        </ListItemText>
                    </MenuItem>
                </Menu>
            </div>
        </Container>
    )
})

ServiceRow.displayName = 'ServiceRow'
